import { Filter, FilterConfig, FilterTypes } from "@/lib/filterable";
import videoService from "@/services/mrfiktiv/services/video.service";
import {
  MrfiktivMuxAssetViewModelGen,
  MrfiktivMuxUploadViewModelGen,
  MrfiktivUpdatePartnerVideoDtoGen,
  MrfiktivVideoViewModelGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { VideoDataAccessLayer } from "@/store/modules/access-layers/video.access-layer";
import { PartnerUserModule } from "@/store/modules/partner-user.store";
import { ITimestamp, Timestamp } from "./timestamp.entity";
import { ConfigModule } from "@/store/modules/config";
import { ITag } from "./tag.entity";
import { IReference, Reference } from "./reference.entity";
import { TagDataAccessLayer } from "@/store/modules/access-layers/tag.access-layer";
import { DetailFormComponentsEnum } from "@/lib/enum/detail-form-components.enum";
import { RulesEnum } from "@/lib/rules/rules.map";
import { FormConfig } from "@/lib/formable";
import { VideoMetadata } from "./video-metadata.entity";

class VideoBase {
  /** The unique identifier of the video */
  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.Video.id"
  })
  id: string;

  partnerId: string;

  environment: string;

  token?: string;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.ticket.userId",
    config: {
      itemCallback: () => PartnerUserModule.paginationList,
      mapItemToComponent: item => ({ item }),
      itemValue: "id",
      component: "refs-user"
    }
  })
  userId?: string | undefined;

  upload?: MrfiktivMuxUploadViewModelGen | undefined;
  asset?: MrfiktivMuxAssetViewModelGen | undefined;

  @FilterConfig({
    type: Timestamp
  })
  timestamp: ITimestamp;

  metadata: VideoMetadata;

  @FormConfig({
    category: "video.metadata",
    searchKeywords: ["objects.video.title"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: { label: "objects.video.title" },
    rules: [RulesEnum.REQUIRED_RULE]
  })
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.video.title"
  })
  title?: string;

  @FormConfig({
    category: "video.metadata",
    searchKeywords: ["objects.video.description"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: { label: "objects.video.description" },
    clearable: true
  })
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.video.description"
  })
  description?: string;

  /**
   * References
   */
  @FilterConfig({ type: Reference })
  refs: IReference[] = [];

  /**
   * Tags
   */
  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.video.tagIds",
    config: {
      itemCallback: () => TagDataAccessLayer.entities,
      mapItemToComponent: item => ({ value: item, size: "small" }),
      itemValue: "id",
      component: "tag-chip"
    }
  })
  tagIds: string[] = [];

  tags: ITag[] = [];

  get hasAsset() {
    return !!(this && this.asset && Array.isArray(this.asset.playback_ids) && this.asset.playback_ids.length > 0);
  }

  get src() {
    if (!this.playbackId) {
      return undefined;
    }

    let src = `https://stream.mux.com/${this.playbackId}.m3u8`;

    if (this.token) {
      src += `?token=${this.token}`;
    }

    return src;
  }

  get muxPlayerSrc() {
    const base = `https://player.mux.com/${this.playbackId}`;

    const params: string[] = [];

    if (this.token) {
      params.push(`playback-token=${encodeURIComponent(this.token)}`);
    }

    const accent = ConfigModule.color?.primary;
    if (accent) {
      params.push(`accent-color=${encodeURIComponent(accent)}`);
    }

    const query = params.join("&");
    return query ? `${base}?${query}` : base;
  }

  get videoJsSrc() {
    if (!this.playbackId) {
      return undefined;
    }

    let src = this.playbackId;

    if (this.token) {
      src += `?token=${this.token}`;
    }

    return src;
  }

  get playbackId() {
    if (!this.hasAsset) return "";

    if (!this.asset?.playback_ids) {
      return undefined;
    }

    return this.asset?.playback_ids[0]?.id;
  }

  get uploadUrl() {
    return this.upload?.url;
  }

  get displayTitle() {
    return this.title?.trim() || this.id;
  }

  constructor(video: Partial<MrfiktivVideoViewModelGen>) {
    this.id = video?.id ?? "";
    this.partnerId = video?.partnerId ?? "";
    this.environment = video?.environment ?? "";
    this.userId = video?.userId;
    this.upload = video?.upload;
    this.asset = video?.asset;
    this.token = video?.token;
    this.timestamp = new Timestamp(video?.timestamp);

    this.metadata = new VideoMetadata(video?.metadata);

    this.title = this.metadata.title;
    this.description = this.metadata.description;
    this.refs = this.metadata.refs;
    this.tagIds = this.metadata.tagIds;
    this.tags = this.metadata.tags;
  }

  map(video?: MrfiktivVideoViewModelGen) {
    this.id = video?.id ?? "";
    this.partnerId = video?.partnerId ?? "";
    this.environment = video?.environment ?? "";
    this.userId = video?.userId ?? "";
    this.upload = video?.upload;
    this.asset = video?.asset;
    this.token = video?.token;

    this.timestamp = new Timestamp(video?.timestamp);

    this.metadata = new VideoMetadata(video?.metadata);

    this.title = this.metadata.title;
    this.description = this.metadata.description;
    this.refs = this.metadata.refs;
    this.tagIds = this.metadata.tagIds;
    this.tags = this.metadata.tags;
  }

  async fetch(): Promise<this> {
    const res = await videoService.getOne(this.partnerId, this.id);

    this.map(res);

    VideoDataAccessLayer.set(this);

    return this;
  }

  async delete(): Promise<this> {
    await videoService.delete(this.partnerId, this.id);

    VideoDataAccessLayer.delete(this);

    return this;
  }

  async cancel(): Promise<this> {
    const res = await videoService.cancel(this.partnerId, this.id);

    this.map(res);

    return this;
  }

  async updateMetadata(dto: MrfiktivUpdatePartnerVideoDtoGen): Promise<this> {
    const updated = await videoService.updateMetadata(this.partnerId, this.id, dto);
    this.map(updated);
    VideoDataAccessLayer.set(this);
    return this;
  }

  async updatePartial(dto: { tagIds?: (string | ITag)[]; title?: string; description?: string; refs?: IReference[] }) {
    if (dto.title !== undefined) this.metadata.title = dto.title;
    if (dto.description !== undefined) this.metadata.description = dto.description;
    if (dto.tagIds !== undefined) {
      this.metadata.tagIds = dto.tagIds.map(tag => (typeof tag === "string" ? tag : tag.id));
    }

    if (dto.refs !== undefined) this.metadata.refs = dto.refs;

    const updated = await videoService.updateMetadata(this.partnerId, this.id, {
      metadata: this.metadata.toDto()
    });

    this.map(updated);
    VideoDataAccessLayer.set(this);

    return this;
  }
}

type IVideo = VideoBase;
const Video = Filter.createForClass(VideoBase);

export { IVideo, Video };
